import React, {useState} from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

//sdk
import { RecordDocumentService, DegreeAndCertificateService } from '@sdk-point/talisis';

//uikit
import { makeStyles, alpha } from '@material-ui/core/styles';
import { Box, Button, Icon, Typography, IconButton, Dialog, DialogTitle, TextField, Checkbox, FormControlLabel, InputAdornment} from '@material-ui/core';

//desing System
import Select from '../../../design-system/components/Form/Select/Select';
import Picker from '../../../shared/components/Form/Picker';
import LoaderContent from '../../../shared/components/Loader/LoaderContent';

const useStyles = makeStyles((theme) => ({
    dialogTitle:{
        display: 'flex',
        alignItems: 'center',
        borderBottom: `1px solid rgba(119, 119, 119, 0.15)`,
        '& .MuiTypography-root':{
            flex: 1,
            textAlign: 'center'
        }
    }
}));

const COMPROMISE_LETTER = process.env.REACT_APP_ENV === 'dev' ? 124 : 92; // ID dinámico basado en el ambiente
const COMPROBANTE_ENVIO = process.env.REACT_APP_ENV === 'dev' ? 127 : 94; // ID dinámico basado en el ambiente
const ACUSE_RECEPCION_DOCUMENTS = process.env.REACT_APP_ENV === 'dev' ? 128 : 93; // ID dinámico basado en el ambiente

const DegreeServiceSDK = new DegreeAndCertificateService(process.env.REACT_APP_ENV, process.env.REACT_APP_X_API_KEY);
const RecordDocumentServiceSDK = new RecordDocumentService(process.env.REACT_APP_ENV, process.env.REACT_APP_X_API_KEY);

const NewDocumentButton = ({onClickNewDoc, listDocument, documentPendientes, documentosPendienteRecepcion, student, programCompanyId}) => {
    const { user } = useSelector((state) => state.userReducer);

    const classes = useStyles();
    const [isLoading, setIsLoading] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [value, setValue] = React.useState("");
    const [selectedDocuments, setSelectedDocuments] = React.useState([]); 
    const [deadline, setDeadline] = React.useState("");
    const [error, setError] = React.useState({ deadline: false, documents: false, campus: false, documentsAcuse: false }); 
    const [sedes, setSedes] = React.useState([]); 
    const [sedeSelected, setSedeSelected] = React.useState(""); 

    const getCampus = React.useCallback(async (ou) => {
        try {
            const response = await DegreeServiceSDK.postCatalogueDynamic(ou, "campus");
            const campusList = response.data.map((c, index) => ({
                id: index + 1, 
                label: c.description, 
                name: c.description 
            }));
            setSedes(campusList); // Actualiza el estado con la lista de campus
        } catch (err) {
            console.error('Error al obtener los campus:', err);
            setError((prevError) => ({
                ...prevError,
                campus: true
            }));
        }
    }, []);

    // useEffect para cargar los campus al montar el componente
    React.useEffect(() => {
        if(sedes.length === 0){
            getCampus(programCompanyId);
        }
    },[getCampus, programCompanyId]);

    // Filtrar el documento con id 124 si no hay documentos pendientes
    const filteredListDocument = documentPendientes[0]?.documents.length === 0 
        ? listDocument.filter(doc => doc.id !== COMPROMISE_LETTER && doc.id !== COMPROBANTE_ENVIO) 
        : listDocument.filter(doc => doc.id !== COMPROBANTE_ENVIO);

    const handleClose = () => {
        setValue('');
        setSelectedDocuments([]);
        setDeadline('');
        setOpen(false);
    };

    const handleClickOpen = () => {
        setValue('');
        setSelectedDocuments([]);
        setDeadline('');
        setOpen(true);
    };

    const handleChangeTypeDocs = (e) => {
        setValue(e.target.value);
    };
    
    const handleChangeSedes = (e) => {

        setSedeSelected(e.target.value);
    };

    const handleCheckboxChange = (e) => {
        const { value, checked } = e.target;
        setSelectedDocuments((prev) =>
            checked
                ? [...prev, { name: value }] // Agrega un objeto con la clave `name`
                : prev.filter((doc) => doc.name !== value) // Filtra por la clave `name`
        );
    };

    const isCheckboxChecked = (documentName) => {
        return selectedDocuments.some((doc) => doc.name === documentName); // Verifica si el documento está seleccionado
    };

    const handleDeadlineChange = (e) => {
        const rawDate = e.target.value; 
        const [year, month, day] = rawDate.split('-');
        const formattedDate = `${day}/${month}/${year}`; 
        setDeadline(formattedDate);
    };

    const getISODate = () => {
        if (!deadline) return "";
        const [day, month, year] = deadline.split('/');
        return `${year}-${month}-${day}`; 
    };

    const validateForm = () => {
        const hasError = {
            deadline: value === COMPROMISE_LETTER && !deadline, // Error si no hay fecha límite
            documents: value === COMPROMISE_LETTER && selectedDocuments.length === 0, // Error si no hay documentos seleccionados
            campus: value === ACUSE_RECEPCION_DOCUMENTS && !sedeSelected, // Error si no hay campus seleccionado
            documentsAcuse: value === ACUSE_RECEPCION_DOCUMENTS && selectedDocuments.length === 0, // Error si no hay documentos seleccionados
        };
        setError(hasError);
        return !hasError.deadline && !hasError.documents && !hasError.campus && !hasError.documentsAcuse; // Retorna true si no hay errores
    };

    const handleClickCompromiseLetter = async () => {
        if (!validateForm()) return; 
        
        setIsLoading(true);

        let body = {
            files: selectedDocuments,
            committed_date: moment(deadline, 'DD/MM/YYYY').format('DD/MM/YYYY'), 
            student: student.name,
            program_name: documentPendientes[0].documents[0].program_name,
            company_id: documentPendientes[0].documents[0].program_company_id,
        };
    
        try {
            const response = await RecordDocumentServiceSDK.generateAdditionalDocument(COMPROMISE_LETTER, body);
    
            if (response.url) {
                hanldeClickNewDocument();
                handleClose();
    
                setTimeout(() => {
                    const link = document.createElement('a');
                    link.href = response.url;
                    link.setAttribute('download', 'Carta_de_Compromiso.docx'); 
                    link.setAttribute('target', '_blank'); 
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setIsLoading(false);
                }, 300); 
            }
    
        } catch (error) {
            console.error('Error generando y descargando documento:', error);
        }
    };
    
    const handleClickAcuseRecepcionDocs = async () => {
        if (!validateForm()) return; 
    
        setIsLoading(true);


        let body = {
            company_id: programCompanyId,
            reception_campus: sedeSelected,
            matricula: student.matricula,
            program_name: documentosPendienteRecepcion[0].documents[0].program_name,
            student: student.name,
            person_id_student: student.studentId,
            admin_name: user.userName,
            person_id_admin: user.person_id,
            documents_pending_receipt: documentosPendienteRecepcion[0].documents
                .filter((document) => !selectedDocuments.some((doc) => doc.name === document.document_name))
                .map((document) => ({
                    name: document.document_name
            })),
            documents_received: selectedDocuments.map((doc) => ({
                name: doc.name
            }))
        };

        try {
            const response = await RecordDocumentServiceSDK.generateAdditionalDocument(ACUSE_RECEPCION_DOCUMENTS, body);
    
            if (response.url) {
                hanldeClickNewDocument();
                handleClose();
    
                setTimeout(() => {
                    const link = document.createElement('a');
                    link.href = response.url;
                    link.setAttribute('download', 'Acuse_de_recepcion_de_documentos.docx'); 
                    link.setAttribute('target', '_blank'); 
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setIsLoading(false);

                }, 300); 
            }
    
        } catch (error) {
            console.error('Error generando y descargando documento:', error);
        }
    };


    const hanldeClickNewDocument = () => {
        onClickNewDoc(value);
        setOpen(false);
    };

    const handleClickNewDocumentCondition = () => {
        if (value === COMPROMISE_LETTER) {
            handleClickCompromiseLetter();
        } else if (value === ACUSE_RECEPCION_DOCUMENTS) {
            handleClickAcuseRecepcionDocs();
        } else {
            hanldeClickNewDocument();
        }
    }

    return (
        <>
            <Button variant='contained' color='primary' fullWidth onClick={handleClickOpen}>Solicitar un nuevo documento</Button>
            <Dialog onClose={handleClose} aria-labelledby="simple-dialog-title" open={open} maxWidth="xs" fullWidth>
                <DialogTitle id="simple-dialog-title" disableTypography className={classes.dialogTitle}>
                    <Typography variant="subtitle1">Solicitar documento nuevo</Typography>
                    <IconButton aria-label="close" className='p-0' onClick={handleClose}>
                        <Icon className='ri-close-line text-fill-stroke-100' style={{fontSize: 24}}/>
                    </IconButton>
                </DialogTitle>
                
                {isLoading && <LoaderContent message="Procesando solicitud..." />}
                
                <Box p={3}>
                    <Select 
                        label="Selecciona el documento a solicitar"
                        value={value}
                        name="newStatus"
                        placeholder="Seleccionar documento"
                        required
                        items={filteredListDocument} // Usar la lista filtrada
                        onChange={handleChangeTypeDocs}
                    />

                    {/* Formulario exclusivo para la creacion de la carta compromiso */}
                    {value === COMPROMISE_LETTER && (
                        <div style={{ marginTop: '20px', gap: '20px', display: 'flex', flexDirection: 'column' }}>
                            <Picker
                                className="date-picker"
                                name="deadline"
                                value={deadline ? moment(deadline, 'DD/MM/YYYY').toDate() : null} 
                                onChange={(event) => {
                                    const rawDate = event.target.value?._d || event.target.value; 
                                    if (rawDate) {
                                        const formattedDate = moment(rawDate).format('DD/MM/YYYY'); 
                                        setDeadline(formattedDate); 
                                        setError((prevError) => ({ ...prevError, deadline: false }));
                                    }
                                }}
                                format="DD/MM/YYYY"
                                placeholder="DD/MM/YYYY"
                                autoOk={true}
                                views={['year', 'month', 'date']}
                                minDate={new Date()} // Disable dates before today
                                keyboardbuttonprops={{
                                    "aria-label": "change date"
                                }}
                                error={error.deadline}
                                helperText={error.deadline ? "La fecha límite es obligatoria" : ""}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Icon className="ri-calendar-2-line" style={{ fontSize: 18 }} />
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <Typography variant='body1' style={{ fontSize: '14px', fontWeight: 600 }}>Documentos pendientes de entrega</Typography>
                            {
                                documentPendientes[0].documents.map((document, index) => (
                                    <FormControlLabel
                                        key={index}
                                        className={classes.checkbox}
                                        control={
                                            <Checkbox
                                                checked={isCheckboxChecked(document.document_name)} // Usa la función para verificar si está marcado
                                                value={document.document_name}
                                                name={`document${index}`}
                                                color="primary"
                                                onChange={handleCheckboxChange}
                                            />
                                        }
                                        label={<Typography variant='body1' className='semi-bold body-text-samll-xs'>{document.document_name}</Typography>}
                                    />
                                ))
                            }
                            {error.documents && (
                                <Typography color="error" variant="body2">
                                    Debes seleccionar al menos un documento.
                                </Typography>
                            )}
                        </div>
                    )}

                    {/* Formulario para generacion de acuse de recepcion de documentos */}
                    {value === ACUSE_RECEPCION_DOCUMENTS && (
                        <div style={{ marginTop: '20px', gap: '20px', display: 'flex', flexDirection: 'column' }}>
                            <Select 
                                label="Seleccionar el campus de recepción"
                                value={sedeSelected}
                                name="newStatus"
                                placeholder="Seleccionar el campus"
                                required
                                items={sedes.map(campus => ({
                                    id: campus.name,
                                    name: campus.name,
                                    label: campus.label
                                }))}
                                onChange={handleChangeSedes}
                                error={error.campus} // Mostrar error si no se selecciona campus
                                helperText={error.campus ? "El campus es obligatorio" : ""}
                            />

                            <Typography variant='body1' style={{ fontSize: '14px', fontWeight: 600 }}>Documentos pendientes de entrega</Typography>

                            {
                                documentosPendienteRecepcion[0].documents.map((document, index) => (
                                    <FormControlLabel
                                        key={index}
                                        className={classes.checkbox}
                                        control={
                                            <Checkbox
                                                checked={isCheckboxChecked(document.document_name)} // Usa la función para verificar si está marcado
                                                value={document.document_name}
                                                name={`document${index}`}
                                                color="primary"
                                                onChange={handleCheckboxChange}
                                            />
                                        }
                                        label={<Typography variant='body1' className='semi-bold body-text-samll-xs'>{document.document_name}</Typography>}
                                    />
                                ))
                            }
                            {error.documentsAcuse && (
                                <Typography color="error" variant="body2">
                                    Debes seleccionar al menos un documento.
                                </Typography>
                            )}
                        </div>
                    )}

                    <Button variant='contained' color='primary' fullWidth 
                        className='offset-mt-4' disabled={!value} 
                        onClick={handleClickNewDocumentCondition}
                        >
                        Solicitar el documento
                    </Button> 
                </Box>
            </Dialog>
        </>
    );
};

export default NewDocumentButton;